import { FormEvent, useState } from "react";

import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { format, isAfter, isBefore, sub } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { MenuItem, Stack } from "@mui/material";
import {
  DatePickerContainer,
  DatePickerField,
  Field,
  FlexDivCustom,
  Form,
  StyledTextField,
} from "../styles";
import { FilterActions } from "../../FilterActions";
import { formatInputValue } from "../../../../../Utils/formatInputValue";
import { simpleConvertToCents } from "../../../../../Utils/formatters";
import { useToast } from "../../../../../Hooks/toast";
import { cleanTaxIDMask, handleTaxIDInput } from "../../../../../Utils";
import { useFilter } from "../../../../../Hooks/useFilter";

interface PixTablesFilterProps {
  tableName: string;
  setPageToFirst: () => void;
}

export const PixTablesFilter = ({
  tableName,
  setPageToFirst,
}: PixTablesFilterProps) => {
  const { getFilterObj, handleApplyFilters, handleRemoveFilters } = useFilter();

  const filterObj = getFilterObj(tableName) as any;

  const { addToast } = useToast();

  const [endToEndId, setEndToEndId] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(() => {
    return filterObj?.startDate !== null &&
      filterObj?.startDate !== undefined &&
      filterObj?.startDate !== ""
      ? new Date(filterObj.startDate.replace(/-/g, "/").replace(/T.+/, ""))
      : new Date();
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    return filterObj?.endDate !== null &&
      filterObj?.endDate !== undefined &&
      filterObj?.endDate !== ""
      ? new Date(filterObj.endDate.replace(/-/g, "/").replace(/T.+/, ""))
      : new Date();
  });
  const [startAmount, setStartAmount] = useState<string | null>(null);
  const [endAmount, setEndAmount] = useState<string | null>(null);
  const [externalId, setExternalId] = useState<string>("");
  const [id, setId] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [payerName, setPayerName] = useState<string>("");
  const [payerDocument, setPayerDocument] = useState<string>("");

  const handleCustomStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleCustomEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleDeleteFilters = () => {
    if (Object.keys(filterObj).length === 0) {
      addToast({
        title: "Nenhum filtro aplicado.",
        description: "Não tem filtro aplicado para ser removido.",
        type: "info",
      });
      return;
    }

    handleRemoveFilters(tableName, {
      startDate: format(new Date(), "yyyy-MM-dd"),
      endDate: format(new Date(), "yyyy-MM-dd"),
      endToEndId: "",
      startAmount: "",
      endAmount: "",
      id: "",
      externalId: "",
      name: "",
      document: "",
      status: "",
    });
    setEndToEndId("");
    setExternalId("");
    setStartDate(new Date());
    setEndDate(new Date());
    setId("");
    setStatus("");
    setPayerName("");
    setPayerDocument("");
    setStartAmount(null);
    setEndAmount(null);
  };

  const handleSubmitForm = (e: FormEvent) => {
    e.preventDefault();

    const queryParams: any = {
      startDate: startDate === null ? "" : format(startDate, "yyyy-MM-dd"),
      endDate: endDate === null ? "" : format(endDate, "yyyy-MM-dd"),
      endToEndId: endToEndId.trim(),
      startAmount:
        startAmount === null ? "" : simpleConvertToCents(startAmount),
      endAmount: endAmount === null ? "" : simpleConvertToCents(endAmount),
      id: id.trim(),
      externalId: externalId.trim(),
      name: payerName.trim(),
      document: cleanTaxIDMask(payerDocument.trim()),
      status: status,
    };

    const hasValue = Object.keys(queryParams).some((key) => !!queryParams[key]);

    //verifica se algum filtro foi aplicado
    if (!hasValue) {
      addToast({
        type: "info",
        title: "Nenhum filtro foi aplicado!",
        description: "Por favor insira algum parâmetro.",
      });

      return;
    }

    //Verifica se o filtro ja foi aplicado
    if (JSON.stringify(queryParams) === JSON.stringify(filterObj)) {
      addToast({
        type: "info",
        title: "Filtro já aplicado!",
        description: "Por favor insira outros parâmetros.",
      });

      return;
    }
    //Verifica se as datas estão em um formato válido
    const regex = /^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])$/;
    if (
      !regex.test(queryParams.startDate) ||
      isBefore(
        new Date(queryParams.startDate),
        sub(new Date(), { years: 1 })
      ) ||
      isAfter(
        new Date(queryParams.startDate.replace(/-/g, "/").replace(/T.+/, "")),
        new Date()
      ) ||
      !regex.test(queryParams.endDate) ||
      isBefore(
        new Date(queryParams.endDate.replace(/-/g, "/").replace(/T.+/, "")),
        sub(new Date(), { years: 1 })
      ) ||
      isAfter(
        new Date(queryParams.endDate.replace(/-/g, "/").replace(/T.+/, "")),
        new Date()
      )
    ) {
      addToast({
        type: "info",
        title: "Data Inválida!",
        description: "Por favor insira uma data válida.",
      });

      return;
    }

    //Verifica se a data inicial não é maior que a data final
    if (
      isAfter(
        new Date(queryParams.startDate.replace(/-/g, "/").replace(/T.+/, "")),
        new Date(queryParams.endDate.replace(/-/g, "/").replace(/T.+/, ""))
      )
    ) {
      addToast({
        type: "info",
        title: "Data Inválida!",
        description: "Data inicial não pode ser maior que data final.",
      });

      return;
    }

    handleApplyFilters(tableName, queryParams);
    setPageToFirst();
  };

  return (
    <Form onSubmit={handleSubmitForm}>
      <FlexDivCustom>
        <Field>
          <StyledTextField
            id="id"
            label="Identificador PIX"
            value={id}
            onChange={(e) => {
              setId(e.target.value);
            }}
            name="id"
            type="text"
          />
        </Field>
        <Field>
          <StyledTextField
            id="endToEndId"
            label="Código Endtoendid"
            value={endToEndId}
            onChange={(e) => {
              setEndToEndId(e.target.value);
            }}
            name="endToEndId"
            type="text"
          />
        </Field>

        <Field>
          <StyledTextField
            id="Status"
            select
            label="Status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
            }}
          >
            <MenuItem value=""></MenuItem>
            <MenuItem value="paid">Pago</MenuItem>
            <MenuItem value="canceled">Cancelado</MenuItem>
            <MenuItem value="pending">Pendente</MenuItem>
            <MenuItem value="processing">Processando</MenuItem>
            <MenuItem value="refused">Recusado</MenuItem>
            <MenuItem value="rejected">Rejeitado</MenuItem>
            <MenuItem value="refunded">Ressarcido</MenuItem>
            <MenuItem value="expired">Expirado</MenuItem>
          </StyledTextField>
        </Field>
      </FlexDivCustom>

      <FlexDivCustom>
        <Field>
          <StyledTextField
            id="payerName"
            label="Pagador"
            value={payerName}
            onChange={(e) => {
              setPayerName(e.target.value);
            }}
            name="payerName"
            type="text"
          />
        </Field>

        <Field>
          <StyledTextField
            id="payerDocument"
            label="Documento"
            value={payerDocument}
            onChange={(e) => {
              setPayerDocument(handleTaxIDInput(e.target.value));
            }}
            name="payerDocument"
            type="text"
          />
        </Field>
        <Field>
          <StyledTextField
            id="externalId"
            label="External ID"
            value={externalId}
            onChange={(e) => {
              setExternalId(e.target.value);
            }}
            name="externalId"
            type="text"
          />
        </Field>
      </FlexDivCustom>
      <FlexDivCustom>
        <Field>
          <StyledTextField
            id="startAmount"
            label="Valor Min."
            value={startAmount === null ? "" : startAmount}
            onChange={(e) => {
              setStartAmount(formatInputValue(e.target.value));
            }}
            name="startAmount"
            type="text"
            placeholder="Valor (R$)"
            aria-label="minAmount-input"
          />
        </Field>
        <Field>
          <StyledTextField
            id="endAmount"
            variant={"outlined"}
            label={"Valor Máx."}
            value={endAmount === null ? "" : endAmount}
            onChange={(e) => {
              setEndAmount(formatInputValue(e.target.value));
            }}
            name="endAmount"
            type="text"
            placeholder="Valor (R$)"
            aria-label="maxAmount-input"
          />
        </Field>
      </FlexDivCustom>
      <DatePickerContainer>
        <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
          <Stack
            direction="row"
            style={{ gap: "2.2rem" }}
            alignItems={"center"}
          >
            <Field>
              <DesktopDatePicker
                label="Data inicial"
                value={startDate}
                minDate={sub(new Date(), { years: 1 })}
                maxDate={new Date()}
                onChange={handleCustomStartDateChange}
                renderInput={(params: any) => <DatePickerField {...params} />}
              />
            </Field>
            <Field>
              <DesktopDatePicker
                label="Data final"
                value={endDate}
                minDate={sub(new Date(), { years: 1 })}
                maxDate={new Date()}
                onChange={handleCustomEndDateChange}
                renderInput={(params: any) => <DatePickerField {...params} />}
              />
            </Field>
          </Stack>
        </LocalizationProvider>
      </DatePickerContainer>
      <FilterActions onClear={handleDeleteFilters} />
    </Form>
  );
};
